// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// 🔴 2026-10-28 · ENTRAR NA VERSÃO CERTA DA APLICAÇÃO
// ============================================================================
//
// 📗 Cliente: *«pós-login, o primeiro botão que eu clico faz com que a app meio que dê F5 e volte
//    ao início… fechando o botão»*. A causa: o ecrã de entrada estava aberto de uma versão ANTERIOR
//    (publica-se várias vezes por dia), e entrar não recarrega a página — a aplicação continuava na
//    versão velha. O primeiro clique numa janela pedia um pedaço de código que já não existe, e a
//    rede de 2026-10-23 (`janela-diferida.tsx`) recarregava a página: a janela fechava-se.
//
// ➡️ Ao passar para dentro (depois de entrar, de mudar a senha ou de voltar a entrar), compara-se o
//    guião principal desta página com o do `index.html` que o servidor serve agora; se diferirem,
//    recarrega-se JÁ — antes de haver janela aberta, que é quando a recarga não custa nada. A sessão
//    fica (o cookie), logo a pessoa reaparece dentro, na versão nova.
//    · Numa página acabada de carregar as duas coincidem: nada acontece.
//    · Uma marca na sessão do separador impede recarregar em ciclo.
//    · Se a leitura falhar, entra-se na mesma — a verificação nunca impede a entrada.
// ============================================================================

const MARCA = 'mpt-recarregou-na-entrada';

/** O guião principal (`/assets/index-XXXX.js`) de um `index.html`, ou `null`. */
export function guiaoPrincipal(html: string): string | null {
  return /<script[^>]*\ssrc="(\/assets\/[^"]+\.js)"/.exec(html)?.[1] ?? null;
}

/** As operações do navegador, injectáveis para as provas. */
export interface NavegadorDaVersao {
  readonly guiaoActual: () => string;
  readonly lerIndex: () => Promise<string>;
  readonly lerMarca: () => string | null;
  readonly gravarMarca: (v: string) => void;
  readonly recarregar: () => void;
}

const NAVEGADOR: NavegadorDaVersao = {
  guiaoActual: () =>
    document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute('src') ?? '',
  // ⚠️ A falha trata-se aqui (D21): uma verificação de versão que não consegue ler o servidor
  //    devolve vazio e deixa entrar — não há frase a mostrar a quem acabou de entrar, e impedir a
  //    entrada por causa disto seria pior do que a recarga que esta verificação existe para evitar.
  lerIndex: async () => {
    try {
      return await (await fetch('/', { cache: 'no-store' })).text();
    } catch {
      return '';
    }
  },
  lerMarca: () => {
    try {
      return sessionStorage.getItem(MARCA);
    } catch {
      return null;
    }
  },
  gravarMarca: (v) => {
    try {
      sessionStorage.setItem(MARCA, v);
    } catch {
      /* sem armazenamento: recarrega na mesma, uma vez */
    }
  },
  recarregar: () => window.location.reload(),
};

/**
 * `true` = pode entrar com esta página; `false` = a página é de uma versão anterior e vai
 * recarregar (quem chama não deve desenhar a aplicação).
 */
export async function entrarNaVersaoCerta(nav: NavegadorDaVersao = NAVEGADOR): Promise<boolean> {
  const actual = nav.guiaoActual();
  // Em desenvolvimento local o guião não vem de /assets/: não há versões a comparar.
  if (actual === '') return true;
  let doServidor: string | null;
  try {
    doServidor = guiaoPrincipal(await nav.lerIndex());
  } catch {
    return true;
  }
  if (doServidor === null || doServidor === actual) return true;
  if (nav.lerMarca() !== null) return true;
  nav.gravarMarca(String(Date.now()));
  nav.recarregar();
  return false;
}
